import { useState, useCallback } from 'react';
import { Popover, Box, Typography, Switch } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { PageHeaderAction } from '../../../../shared/components/layout/PageHeaderAction';

interface SitePlotsDisplayMenuProps {
  showValues: boolean;
  onShowValuesChange: (value: boolean) => void;
}

/**
 * SitePlotsDisplayMenu — Linear-style "Display" popover that lets users
 * toggle whether values (budget / price) are shown on plots and labour items.
 */
export function SitePlotsDisplayMenu({
  showValues,
  onShowValuesChange,
}: SitePlotsDisplayMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <PageHeaderAction
        label="Display"
        onClick={handleOpen}
        icon={<TuneIcon />}
        aria-haspopup={true}
        aria-expanded={open}
        aria-controls={open ? 'site-plots-display-popover' : undefined}
        data-testid="site-plots-display-button"
      />
      <Popover
        id="site-plots-display-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 280,
              borderRadius: 2,
              p: 2,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Show values
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={showValues}
            onChange={(_, checked) => onShowValuesChange(checked)}
            aria-label="Show values"
            data-testid="site-plots-show-values-toggle"
          />
        </Box>
      </Popover>
    </>
  );
}
